/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text } from '@components/atoms'

import { TextField, Switch, FormControlLabel, Button, Chip, Tooltip } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { InputAdornment } from '@mui/material'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'

import { SeasonType, SeasonModificationType } from '@schemas'
import dayjs from 'dayjs'

/**
 * 시즌 개요 — 시즌 정보 편집 + 실적 지표.
 *
 * `/main/season/:id/edit` 에 있던 필드를 목록 화면으로 끌어왔다. 상품을 손보면서 시즌
 * 자체(기간·목표)를 같이 보는 일이 잦은데, 그때마다 전체 화면으로 나갔다 오는 게 번거로웠다.
 * 판매 시작/중지는 여기 두지 않는다 — 되돌리기 어려운 조작이라 확인 모달이 붙은 수정 화면에
 * 그대로 둔다.
 *
 * 지표는 차트가 아니라 **stat tile** 이다. 하나의 hero(매출액)와 보조 타일 셋으로 읽고,
 * 값에는 색을 입히지 않는다 — 색은 옆의 칩과 미터가 나른다.
 */

interface SeasonOverviewProps {
	season: SeasonType
	onSave: (body: SeasonModificationType) => void
	/** 판매 시작. 기간 밖이면 호출부가 경고를 띄우고 서버까지 가지 않는다. */
	onOpenSale: () => void
	/** 판매 중지. shop 의 전 상품이 Sold Out 이 된다. */
	onCloseSale: () => void
	onDelete: () => void
}

const usd = (value: number): string =>
	value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

/** 큰 값은 자릿수를 줄여 읽는다. 타일은 정확한 원 단위를 보는 자리가 아니다. */
const compactUsd = (value: number): string => {
	if (Math.abs(value) >= 1_000_000) {
		return `$${(value / 1_000_000).toLocaleString('en-US', {
			maximumFractionDigits: 1,
		})}M`
	}
	if (Math.abs(value) >= 10_000) {
		return `$${(value / 1_000).toLocaleString('en-US', { maximumFractionDigits: 1 })}K`
	}
	return usd(value)
}

const toDateInput = (value: string): string =>
	value === '' || value === null ? '' : dayjs(value).format('YYYY-MM-DD')

export const SeasonOverview: React.FC<SeasonOverviewProps> = ({
	season,
	onSave,
	onOpenSale,
	onCloseSale,
	onDelete,
}) => {
	const [form, setForm] = React.useState<SeasonModificationType>({
		title: '',
		startDate: '',
		endDate: '',
		activeYn: 0,
		targetUsd: 0,
		targetQuantity: 0,
	})

	React.useEffect(() => {
		setForm({
			title: season.title ?? '',
			startDate: toDateInput(season.startDate),
			endDate: toDateInput(season.endDate),
			activeYn: season.activeYn ?? 0,
			targetUsd: season.targetUsd ?? 0,
			targetQuantity: season.targetQuantity ?? 0,
		})
	}, [season])

	/** 손댄 것이 있을 때만 저장을 연다 — 누를 수 있는 버튼은 누를 이유가 있어야 한다. */
	const isDirty: boolean = React.useMemo(
		() =>
			form.title !== (season.title ?? '') ||
			form.startDate !== toDateInput(season.startDate) ||
			form.endDate !== toDateInput(season.endDate) ||
			form.activeYn !== (season.activeYn ?? 0) ||
			Number(form.targetUsd) !== (season.targetUsd ?? 0) ||
			Number(form.targetQuantity) !== (season.targetQuantity ?? 0),
		[form, season],
	)

	const edit = (field: keyof SeasonModificationType, value: string | number) => {
		setForm(prev => ({ ...prev, [field]: value }))
	}

	const isOpen: boolean = season.openYn === 1

	/** 판매 기간 안인가. 기간 밖에서는 시작할 수 없다 (SEASON_OPEN_PLAN §4). */
	const isInSalePeriod: boolean = React.useMemo(() => {
		if (!season.startDate || !season.endDate) return false
		const today = dayjs().startOf('day')
		return (
			!today.isBefore(dayjs(season.startDate).startOf('day')) &&
			!today.isAfter(dayjs(season.endDate).startOf('day'))
		)
	}, [season.startDate, season.endDate])

	/*
		VAT 는 서버가 `vatUsd` 로 내려준다. 전에는 화면이 `paidUsd * 10 / 110` 으로 다시
		셌는데, 같은 값을 두 곳에서 계산하면 세율이 바뀔 때 한쪽만 고쳐진다.

		`공급가액`은 매출에서 VAT 를 뺀 값이다. 전에 `영업이익` 이라 불렀지만 **원가가
		빠지지 않아** 이익이 아니다 — 원가는 BOM 에 있고 아직 시즌 실적에 연결되지 않았다.
	*/
	const vatUsd: number = season.vatUsd ?? 0
	const supplyUsd: number = (season.paidUsd ?? 0) - vatUsd

	const usdProgress: number =
		season.targetUsd > 0 ? ((season.paidUsd ?? 0) / season.targetUsd) * 100 : 0
	const qtyProgress: number =
		season.targetQuantity > 0
			? ((season.soldQuantity ?? 0) / season.targetQuantity) * 100
			: 0

	const renderMeter = (label: string, percent: number, detail: string) => {
		const reached = percent >= 100
		return (
			<View className="meterView">
				<View className="meterHead">
					<Text category="p2" className="meterLabel">
						{label}
					</Text>
					<Text category="s2" className={`meterPercent ${reached ? 'reached' : ''}`}>
						{`${percent.toLocaleString('en-US', { maximumFractionDigits: 0 })}%`}
					</Text>
				</View>
				<View className="meterTrack">
					<View
						className={`meterFill ${reached ? 'reached' : ''}`}
						style={{ width: `${Math.min(percent, 100)}%` }}
					/>
				</View>
				<Text category="p2" className="meterDetail">
					{detail}
				</Text>
			</View>
		)
	}

	return (
		<View css={style}>
			{/* ── 시즌 정보 ───────────────────────────────────────────── */}
			<View className="panelView">
				<View className="panelHead">
					<Text category="s1" className="panelTitle">
						시즌 정보
					</Text>
					{isOpen && <Chip size="small" label="판매중" />}
					<View className="panelSpacer" />
					<Button
						variant="contained"
						size="small"
						onClick={() => onSave(form)}
						disabled={!isDirty}
					>
						저장
					</Button>
				</View>

				<View className="fieldRow">
					<TextField
						label="시즌 제목"
						value={form.title}
						onChange={e => edit('title', e.target.value)}
						size="small"
						className="titleField"
					/>
					<DatePicker
						label="시작 날짜"
						value={form.startDate === '' ? null : dayjs(form.startDate).toDate()}
						onChange={value =>
							edit('startDate', value === null ? '' : dayjs(value).format('YYYY-MM-DD'))
						}
						format="yyyy-MM-dd"
						slotProps={{ textField: { size: 'small', className: 'dateField' } }}
					/>
					<DatePicker
						label="종료 날짜"
						value={form.endDate === '' ? null : dayjs(form.endDate).toDate()}
						onChange={value =>
							edit('endDate', value === null ? '' : dayjs(value).format('YYYY-MM-DD'))
						}
						format="yyyy-MM-dd"
						slotProps={{ textField: { size: 'small', className: 'dateField' } }}
					/>
					<TextField
						type="number"
						label="목표 금액"
						value={form.targetUsd}
						onChange={e => edit('targetUsd', Number(e.target.value))}
						size="small"
						className="numberField"
						InputProps={{
							startAdornment: <InputAdornment position="start">$</InputAdornment>,
						}}
						inputProps={{ min: 0, step: 1 }}
					/>
					<TextField
						type="number"
						label="목표 수량"
						value={form.targetQuantity}
						onChange={e => edit('targetQuantity', Number(e.target.value))}
						size="small"
						className="numberField"
						inputProps={{ min: 0, step: 1 }}
					/>
					<FormControlLabel
						control={
							<Switch
								checked={form.activeYn === 1}
								onChange={e => edit('activeYn', e.target.checked ? 1 : 0)}
							/>
						}
						label="활성화"
					/>
				</View>

				{/*
					되돌리기 어려운 조작은 입력 필드와 섞지 않고 박스 아래에 따로 모은다.
					둘 다 누르면 바로 적용되지 않고 확인 모달을 한 번 거친다.
				*/}
				<View className="dangerRow">
					<Text category="p2" className="dangerHint">
						{isOpen
							? '지금 shop 에 노출되고 있다. 중지하면 shop 의 전 상품이 Sold Out 이 된다.'
							: '판매중인 시즌은 항상 하나다. 다른 시즌이 판매중이면 자동으로 중지된다.'}
					</Text>
					<View className="panelSpacer" />
					{isOpen ? (
						<Button variant="outlined" size="small" onClick={onCloseSale}>
							판매 중지
						</Button>
					) : (
						<Tooltip title={isInSalePeriod ? '' : '오늘은 이 시즌의 판매 기간이 아니다'}>
							<span>
								<Button variant="outlined" size="small" onClick={onOpenSale}>
									판매 시작
								</Button>
							</span>
						</Tooltip>
					)}
					<Button variant="text" size="small" onClick={onDelete}>
						삭제
					</Button>
				</View>
			</View>

			{/* ── 실적 ────────────────────────────────────────────────── */}
			<View className="panelView">
				<View className="panelHead">
					<Text category="s1" className="panelTitle">
						실적
					</Text>
				</View>

				<View className="metricRow">
					{/* hero — 이 화면이 이끄는 숫자 하나 */}
					<View className="heroView">
						<Text category="p2" className="metricLabel">
							매출액
						</Text>
						<Text className="heroValue">{usd(season.paidUsd ?? 0)}</Text>
						<Text category="p2" className="metricDetail">
							{`판매 ${(season.soldQuantity ?? 0).toLocaleString()}개`}
						</Text>
					</View>

					<View className="tileGroup">
						<View className="tileView">
							<Text category="p2" className="metricLabel">
								VAT
							</Text>
							<Text className="tileValue">{compactUsd(vatUsd)}</Text>
						</View>
						<View className="tileView">
							<Text category="p2" className="metricLabel">
								할인금액
							</Text>
							<Text className="tileValue">{compactUsd(season.discountedUsd ?? 0)}</Text>
						</View>
						<View className="tileView">
							<Text category="p2" className="metricLabel">
								공급가액
							</Text>
							<Text className="tileValue">{compactUsd(supplyUsd)}</Text>
							<Text category="p2" className="metricDetail">
								매출 − VAT
							</Text>
						</View>
					</View>
				</View>

				{/* 목표 대비는 실적 숫자를 다 읽은 뒤에 보는 것이라 박스 아래에 깐다 */}
				<View className="meterRow">
					{renderMeter(
						'목표 금액',
						usdProgress,
						`${usd(season.paidUsd ?? 0)} / ${usd(season.targetUsd ?? 0)}`,
					)}
					{renderMeter(
						'목표 수량',
						qtyProgress,
						`${(season.soldQuantity ?? 0).toLocaleString()} / ${(
							season.targetQuantity ?? 0
						).toLocaleString()}개`,
					)}
				</View>
			</View>
		</View>
	)
}

const style = css({
	...boShared,
	display: 'flex',
	flexDirection: 'column',
	gap: 0,
	paddingBottom: 8,

	/*
		Text 는 `div > p` 구조에 가운데 정렬이 기본이다. 그대로 두면 설명이 화면 한복판에
		떠서 어느 줄에 딸린 말인지 알 수 없다. 이 패널의 글은 전부 왼쪽에서 시작한다.
	*/
	p: {
		textAlign: 'left',
		fontFamily: 'inherit',
	},

	// 패널 = 헤어라인 밴드. 박스·라운드·배경 없음 (DESIGN_SYSTEM §4)
	'.panelView': {
		display: 'flex',
		flexDirection: 'column',
		gap: 16,
		padding: '20px 0 24px',
		borderRadius: 0,
		borderBottom: `1px solid ${mono.line}`,
		backgroundColor: 'transparent',
	},
	'.panelHead': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
		minHeight: 30,
	},
	'.panelTitle': {
		fontSize: 11,
		fontWeight: 500,
		letterSpacing: '.24em',
		textTransform: 'uppercase',
		color: mono.faint,
		width: 'auto',
	},
	'.panelSpacer': {
		flex: 1,
	},
	'.dangerRow': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 8,
		marginTop: 4,
		paddingTop: 14,
		borderTop: `1px solid ${mono.line}`,
	},
	'.dangerHint': {
		fontSize: 12.5,
		fontWeight: 300,
		color: mono.dim,
		lineHeight: 1.6,
	},

	// 시즌 정보 — 한 줄에 흐르고 좁아지면 접힌다
	'.fieldRow': {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: 12,

		'.titleField': {
			minWidth: '14rem',
			flex: '1 1 14rem',
		},
		'.dateField': {
			width: '10rem',
		},
		'.numberField': {
			width: '9rem',
		},
	},

	/*
		실적 — hero 하나 + 보조 타일 + 미터. 한 줄에 눕힌다. 세로로 벌어지면 아래의
		상품 테이블이 화면 밖으로 밀려서, 정작 자주 보는 것을 스크롤해야 한다.
	*/
	'.metricRow': {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'flex-start',
		columnGap: 40,
		rowGap: 16,

		p: {
			margin: 0,
		},
	},
	'.metricLabel': {
		fontSize: 11,
		fontWeight: 500,
		letterSpacing: '.14em',
		textTransform: 'uppercase',
		color: mono.faint,
	},
	/*
		값에는 색을 입히지 않는다. 색을 쓰면 네 숫자가 서로 다른 뜻을 가진 것처럼 보이는데
		실제로는 같은 종류의 금액이다 — 구분은 라벨이 한다.
	*/
	'.metricDetail': {
		fontSize: 12,
		fontWeight: 300,
		color: mono.dim,
	},

	'.heroView': {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		minWidth: '9rem',

		'.heroValue': {
			fontSize: 40,
			lineHeight: 1,
			fontWeight: 200,
			color: mono.ink,
			letterSpacing: '-0.015em',
			fontVariantNumeric: 'tabular-nums',
		},
	},

	'.tileGroup': {
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 32,
		alignItems: 'flex-start',
		paddingTop: 4,
	},
	'.tileView': {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
		minWidth: '6rem',

		'.tileValue': {
			fontSize: 24,
			lineHeight: 1,
			fontWeight: 300,
			color: mono.ink,
			fontVariantNumeric: 'tabular-nums',
		},
	},

	// 목표 대비는 박스 하단에 두 칸으로 깐다. 폭이 좁아지면 한 칸씩 쌓인다.
	'.meterRow': {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))',
		columnGap: 40,
		rowGap: 12,
		marginTop: 8,
		paddingTop: 16,
		borderTop: `1px solid ${mono.line}`,

		p: {
			margin: 0,
		},
	},

	'.meterView': {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	'.meterHead': {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'baseline',
		justifyContent: 'space-between',
		gap: 8,
	},
	'.meterLabel': {
		fontSize: 11,
		fontWeight: 500,
		letterSpacing: '.14em',
		textTransform: 'uppercase',
		color: mono.faint,
	},
	'.meterPercent': {
		fontSize: 13,
		fontWeight: 500,
		color: mono.dim,
		fontVariantNumeric: 'tabular-nums',

		'&.reached': {
			color: mono.ink,
		},
	},
	/* 트랙은 헤어라인, 채움은 ink — 도달해도 색이 바뀌지 않는다. 숫자가 말한다 */
	'.meterTrack': {
		height: 2,
		borderRadius: 0,
		backgroundColor: mono.line,
		overflow: 'hidden',
	},
	'.meterFill': {
		height: '100%',
		borderRadius: 0,
		backgroundColor: mono.ink,
		transition: 'width 200ms ease-out',

		'&.reached': {
			backgroundColor: mono.ink,
		},
	},
	'.meterDetail': {
		textAlign: 'left',
		fontSize: 12,
		fontWeight: 300,
		color: mono.dim,
		fontVariantNumeric: 'tabular-nums',
	},
})
